import { useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'

import { cx } from './cx'

/*
 | Etiqueta, campo, ayuda y error en un solo componente.
 |
 | No es azúcar: sueltos, el `htmlFor` y el `aria-describedby` hay que acordarse
 | de escribirlos en cada formulario, y basta con olvidarlos una vez para que un
 | lector de pantalla anuncie un campo sin nombre. Aquí el `id` lo genera
 | `useId()` y las relaciones se atan solas.
 */

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'> {
  label: string
  /** Texto de ayuda permanente. Se anuncia junto a la etiqueta. */
  hint?: string
  /** Mensaje de error. Pone el campo en estado inválido y lo anuncia. */
  error?: string | null
  /** Contenido pegado al campo: un sufijo como «.chasquitambo.com». */
  suffix?: ReactNode
  className?: string
}

export function Field({ label, hint, error, suffix, className, ...input }: Props) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const invalid = typeof error === 'string' && error !== ''

  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-label font-medium text-ink">
        {label}
      </label>

      <div
        className={cx(
          'flex items-stretch overflow-hidden rounded-control border bg-surface transition-colors',
          'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25',
          invalid ? 'border-danger' : 'border-line-strong',
        )}
      >
        <input
          {...input}
          id={id}
          aria-invalid={invalid || undefined}
          aria-describedby={cx(hint !== undefined && hintId, invalid && errorId) || undefined}
          // `min-w-0` es lo que impide que un valor largo desborde el contenedor
          // flex en pantallas de 360 px.
          className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-ink outline-none"
        />

        {suffix !== undefined && (
          <span className="grid shrink-0 place-items-center border-l border-line bg-surface-sunken px-3 text-label text-ink-subtle">
            {suffix}
          </span>
        )}
      </div>

      {hint !== undefined && !invalid && (
        <p id={hintId} className="text-caption text-ink-subtle">
          {hint}
        </p>
      )}

      {invalid && (
        <p id={errorId} role="alert" className="text-caption text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'id'> {
  label: string
  hint?: string
  error?: string | null
  /** Cuántos caracteres caben. Se pinta la cuenta atrás cerca del final. */
  maxLength?: number
  className?: string
}

/**
 * Lo mismo que `Field` pero para texto de varias líneas.
 *
 * Aparte y no como bandera de `Field`: las props de `<input>` y `<textarea>`
 * son tipos distintos, y unirlos obliga a que cada uso de `Field` —que son
 * casi todos los formularios— cargue con una unión que no le sirve de nada.
 *
 * La cuenta de caracteres sólo aparece en el último cuarto. Enseñarla desde el
 * primer carácter convierte un saludo en un examen; enseñarla al llegar al tope
 * hace que el campo deje de aceptar letras sin explicar por qué.
 */
export function Textarea({ label, hint, error, maxLength, className, ...textarea }: TextareaProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const invalid = typeof error === 'string' && error !== ''

  const used = String(textarea.value ?? '').length
  const showCount = maxLength !== undefined && used > maxLength * 0.75

  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-label font-medium text-ink">
        {label}
      </label>

      <div
        className={cx(
          'overflow-hidden rounded-control border bg-surface transition-colors',
          'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25',
          invalid ? 'border-danger' : 'border-line-strong',
        )}
      >
        <textarea
          {...textarea}
          id={id}
          maxLength={maxLength}
          aria-invalid={invalid || undefined}
          aria-describedby={cx(hint !== undefined && hintId, invalid && errorId) || undefined}
          className="block w-full resize-y bg-transparent px-3 py-2.5 text-ink outline-none"
        />
      </div>

      <div className="flex items-start justify-between gap-3">
        {invalid ? (
          <p id={errorId} role="alert" className="text-caption text-danger">
            {error}
          </p>
        ) : (
          hint !== undefined && (
            <p id={hintId} className="text-caption text-ink-subtle">
              {hint}
            </p>
          )
        )}

        {showCount && (
          <p className="shrink-0 text-caption text-ink-subtle" data-numeric aria-hidden>
            {maxLength - used}
          </p>
        )}
      </div>
    </div>
  )
}
